import { Message } from '~/components/component-library'

import type { FieldError, FieldErrors, FieldValues } from 'react-hook-form'

type FlatError = { key: string; message: string }

const isObject = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object'

const isFieldError = (v: unknown): v is FieldError =>
  isObject(v) && ('message' in v || 'type' in v)

/** Join ['a', 0, 'b'] -> "a[0].b"; empty -> "form" */
const joinPath = (path: ReadonlyArray<string | number>): string => {
  if (path.length === 0) return 'form'
  const parts: string[] = []
  path.forEach((seg) => {
    if (typeof seg === 'number') {
      parts[parts.length - 1] = (parts[parts.length - 1] ?? '') + `[${seg}]`
    } else {
      parts.push(seg)
    }
  })
  return parts.join('.')
}

function flattenErrors<T extends FieldValues>(
  errors: FieldErrors<T>,
): FlatError[] {
  const out: FlatError[] = []

  const push = (path: ReadonlyArray<string | number>, msg?: string) => {
    if (msg) out.push({ key: joinPath(path), message: msg })
  }

  const pushTypes = (path: ReadonlyArray<string | number>, node: unknown) => {
    const types = (node as { types?: Record<string, string> } | undefined)
      ?.types
    Object.values(types ?? {}).forEach((m) => push(path, m))
  }

  const visit = (node: unknown, path: ReadonlyArray<string | number>) => {
    if (!isObject(node)) return

    // Leaf: FieldError
    if (isFieldError(node)) {
      push(path, node.message)
      pushTypes(path, node)
      return
    }

    // Array of nested errors
    if (Array.isArray(node)) {
      node.forEach((child, i) => visit(child, [...path, i]))
      return
    }

    // Object of nested errors; special-case ".root"
    Object.entries(node).forEach(([k, v]) => {
      if (k === 'root') {
        if (isFieldError(v)) {
          push(path, (v as FieldError).message)
          pushTypes(path, v)
        }
        return
      }
      visit(v, [...path, k])
    })
  }

  visit(errors, [])
  return out
}

export function ErrorMessages<T extends FieldValues>({
  errors,
}: {
  errors: FieldErrors<T>
}) {
  const flat = flattenErrors<T>(errors)
  if (flat.length === 0) return null

  return (
    <div className="flex w-full flex-col items-start space-y-2 text-xs text-red-400">
      {flat.map(({ key, message }, i) => (
        <Message variant="danger" key={`${key}-${i}`}>
          {message}
        </Message>
      ))}
    </div>
  )
}
